import { Controller } from "@hotwired/stimulus";

/**
 * What a change of FULFILLMENT owes the page root
 * (CAL-499).
 *
 * It sits on the details column, which is what a
 * fulfillment switch replaces, and so it runs exactly
 * when the answer it carries can have changed. Two
 * things live on the root that depend on it, and no
 * stream replaces the root: it holds the customer,
 * fulfillment and payment cards and every unsubmitted
 * field in them, so it cannot be a frame.
 *
 * The FEE, cached by `manual-order-totals`. A switch used
 * to leave a delivery fee behind: the rows came back
 * correct from the server and the next recompute, any
 * tile tap or discount keystroke, added the fee stamped
 * at first paint, putting $45 of delivery on a pickup
 * order.
 *
 * And the ORDER TYPE carried by the customer side form,
 * which is what "Use" submits. A merchant who switched to
 * pickup and then attached a matched customer was
 * silently put back on delivery, losing the switch they
 * had just made.
 *
 * SEPARATE FROM `manual-order-basket` because the two
 * have different triggers, not because they do different
 * work. The basket figure changes on every line mutation
 * and rides a frame every one of them refreshes; the fee
 * and the order type change only on a switch, and a
 * response that does not know the fulfillment must not
 * state them. A courier fee in particular is fetched by
 * the browser and the server never sees one, so a tile
 * tap clearing it would wipe a live quote off the screen.
 *
 * The side form is reached by id rather than by an
 * outlet, the same way `external-form-submitter` reaches
 * it: it is a plain `<form>` with no controller of its
 * own, and it has to be, because it sits outside the
 * order form that forms cannot nest inside.
 */
export default class extends Controller {
  static outlets = ["manual-order-totals"];
  static values = {
    hasFee: Boolean,
    feeCents: Number,
    orderType: String,
    customerFormId: String,
  };

  connect() {
    this.publishFee();
    this.publishOrderType();
  }

  publishFee() {
    if (!this.hasManualOrderTotalsOutlet) return;

    const root =
      this.manualOrderTotalsOutletElement.dataset;
    root.manualOrderTotalsHasFeeValue = this.hasFeeValue;
    root.manualOrderTotalsFeeCentsValue =
      this.feeCentsValue;
  }

  publishOrderType() {
    if (!this.orderTypeValue) return;
    if (!this.customerFormIdValue) return;

    const form = document.getElementById(
      this.customerFormIdValue
    );
    const field =
      form && form.elements.namedItem("order_type");
    if (field) field.value = this.orderTypeValue;
  }
}
